import React from 'react';
import {
  IconTruckDelivery,
  IconMapPin,
  IconPackage,
  IconClipboard,
  IconSearch,
  IconCircleCheck,
  IconClock,
  IconTruck,
  IconChecklist,
  IconHome
} from '@tabler/icons-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { fetchTrackOrderStatus } from '../../redux/slices/client/clientOrderSlice';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import L from 'leaflet';
import markerIcon from '../../assets/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const customMarkerIcon = new L.Icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [60, 60],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const ShimmerEffect = () => (
  <div className="animate-pulse space-y-4">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="h-4 bg-gray-200 rounded-full"></div>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const statusColors = {
    'placed': 'bg-blue-100 text-blue-800',
    'processing': 'bg-yellow-100 text-yellow-800',
    'shipped': 'bg-purple-100 text-purple-800',
    'delivered': 'bg-green-100 text-green-800',
    'default': 'bg-gray-100 text-gray-800'
  };

  const colorClass = statusColors[status.toLowerCase()] || statusColors.default;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
      {status.toLowerCase() === 'placed' && <IconChecklist className="mr-1" size={16} />}
      {status.toLowerCase() === 'processing' && <IconClock className="mr-1" size={16} />}
      {status.toLowerCase() === 'shipped' && <IconTruck className="mr-1" size={16} />}
      {status.toLowerCase() === 'delivered' && <IconCircleCheck className="mr-1" size={16} />}
      {status}
    </span>
  );
};

const TimelineItem = ({ date, status, isLast, isActive }) => (
  <div className="flex items-start gap-4">
    <div className="flex flex-col items-center">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center 
        ${isActive ? 'bg-[#00148c] text-white' : 'bg-gray-200'}`}>
        {isActive ? <IconCircleCheck size={16} /> : null}
      </div>
      {!isLast && (
        <div className={`w-1 h-16 ${isActive ? 'bg-[#00148c]' : 'bg-gray-200'}`}></div>
      )}
    </div>
    <div className="flex-1 pb-6">
      <h3 className="font-medium text-gray-900 capitalize">{status}</h3>
      <p className="text-sm text-gray-500">{date}</p>
    </div>
  </div>
);

export default function OrderTrack() {
  const dispatch = useDispatch();
  const { orderId } = useParams();
  
  const { 
    orderTrackingStatusDetails, 
    orderTrackingStatusDetailsLoading,
    orderTrackingStatusDetailsError 
  } = useSelector((state) => state.userOrder);

  const [inputOrderId, setInputOrderId] = useState(orderId || '');
  const [searchedOrderId, setSearchedOrderId] = useState(orderId || '');

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputOrderId(text);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  const handleSearch = () => {
    if (inputOrderId.trim()) {
      setSearchedOrderId(inputOrderId);
      dispatch(fetchTrackOrderStatus(inputOrderId));
    }
  };

  useEffect(() => {
    if (orderId) {
      setSearchedOrderId(orderId);
      dispatch(fetchTrackOrderStatus(orderId));
    }
  }, [dispatch, orderId]);

  const currentLocation = { lat: 40.7128, lng: -74.006 };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "--/--/----") return "Pending";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIndex = (status) => {
    const statusOrder = ['placed', 'confirmed', 'shipped', 'delivered'];
    return statusOrder.indexOf(status?.toLowerCase()) || 0;
  };

  return (
    <div className="bg-[#F8F5F2] min-h-screen py-10">
      <div className="max-w-6xl mx-auto px-4">
        {/* Title & Subtitle */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#00148c] mb-2">
            Track Your Order
          </h1>
          <p className="text-lg text-[#2F2F2F]">
            {orderTrackingStatusDetails ? 
              "Your Order Is On The Way! It Will Arrive Soon." : 
              "Enter your order ID to track your package"}
          </p>
        </div>

        {/* Input Field for Order ID */}
        <div className="mb-8 text-center">
          <label htmlFor="order-id" className="block text-lg font-medium text-[#00148c] mb-2">
            Enter or Paste Your Order ID
          </label>
          <div className="flex justify-center items-center gap-2">
            <div className="relative">
              <input
                id="order-id"
                type="text"
                value={inputOrderId}
                onChange={(e) => setInputOrderId(e.target.value)}
                className="border border-gray-300 rounded-lg p-3 pl-12 w-72 focus:outline-none focus:ring-2 focus:ring-[#00148c] focus:border-transparent"
                placeholder="Enter Order ID"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handlePasteFromClipboard}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-[#00148c]"
                title="Paste from clipboard"
              >
                <IconClipboard size={22} />
              </button>
            </div>
            <button
              onClick={handleSearch}
              className="bg-[#00148c] text-white px-6 py-3 rounded-lg hover:bg-[#0026d2] flex items-center gap-2 transition-colors duration-200"
            >
              <IconSearch size={20} />
              <span>Search</span>
            </button>
          </div>
          {orderTrackingStatusDetailsError && (
            <p className="text-red-500 mt-2">
              {orderTrackingStatusDetailsError.message || 'Error fetching order details'}
            </p>
          )}
        </div>

        {orderTrackingStatusDetailsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-md h-64">
                <ShimmerEffect />
              </div>
            ))}
          </div>
        ) : orderTrackingStatusDetails ? (
          <>
            {/* ORDER INFO SECTION */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {/* Left Column: Order Status */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[#00148c]/10 rounded-lg">
                    <IconPackage size={24} className="text-[#00148c]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Order Summary
                  </h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Order Number</p>
                    <p className="font-medium">#{searchedOrderId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <StatusBadge status={orderTrackingStatusDetails.order_status?.status || 'Pending'} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Estimated Delivery</p>
                    <p className="font-medium">
                      {formatDate(orderTrackingStatusDetails.order_status?.estimated_delivery_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Carrier</p>
                    <p className="font-medium">
                      {orderTrackingStatusDetails.order_status?.carrier || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tracking Number</p>
                    <p className="font-medium">
                      {orderTrackingStatusDetails.order_status?.tracking_number || 'Not available'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Middle Column: Tracking Progress */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[#00148c]/10 rounded-lg">
                    <IconTruckDelivery size={24} className="text-[#00148c]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Delivery Progress
                  </h2>
                </div>
                <div className="space-y-2">
                  <TimelineItem
                    date={formatDate(orderTrackingStatusDetails.item_activity?.order_placed)}
                    status="Order Placed"
                    isActive={getStatusIndex(orderTrackingStatusDetails.order_status?.status) >= 0}
                  />
                  <TimelineItem
                    date={formatDate(orderTrackingStatusDetails.item_activity?.order_confirmed)}
                    status="Order Confirmed"
                    isActive={getStatusIndex(orderTrackingStatusDetails.order_status?.status) >= 1}
                  />
                  <TimelineItem
                    date={orderTrackingStatusDetails.item_activity?.shipped ? 
                      formatDate(orderTrackingStatusDetails.item_activity.shipped) : 'Pending'}
                    status="Shipped"
                    isActive={getStatusIndex(orderTrackingStatusDetails.order_status?.status) >= 2}
                  />
                  <TimelineItem
                    date={orderTrackingStatusDetails.item_activity?.delivered ? 
                      formatDate(orderTrackingStatusDetails.item_activity.delivered) : 'Pending'}
                    status="Delivered"
                    isLast={true}
                    isActive={getStatusIndex(orderTrackingStatusDetails.order_status?.status) >= 3}
                  />
                </div>
              </div>

              {/* Right Column: Shipping Address */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-[#00148c]/10 rounded-lg">
                    <IconMapPin size={24} className="text-[#00148c]" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Shipping Address
                  </h2>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <IconHome size={20} className="text-gray-400 mt-0.5" />
                    <div>
                      {orderTrackingStatusDetails.shipping_address?.address_line_1 && (
                        <p className="font-medium">{orderTrackingStatusDetails.shipping_address.address_line_1}</p>
                      )}
                      {orderTrackingStatusDetails.shipping_address?.address_line_2 && (
                        <p className="font-medium">{orderTrackingStatusDetails.shipping_address.address_line_2}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <IconMapPin size={20} className="text-gray-400 mt-0.5" />
                    <p className="font-medium">
                      {[
                        orderTrackingStatusDetails.shipping_address?.city,
                        orderTrackingStatusDetails.shipping_address?.state,
                        orderTrackingStatusDetails.shipping_address?.zip_code
                      ].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  {orderTrackingStatusDetails.shipping_address?.country && (
                    <div className="flex items-center gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="font-medium">{orderTrackingStatusDetails.shipping_address.country}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* TRACK IN REAL-TIME */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#00148c] mb-2">
                Track in Real-Time
              </h2>
              <p className="mx-auto w-full max-w-md text-gray-600">
                Keep an eye on your package's journey to your doorstep.
              </p>
            </div>
            <div className="w-full h-96 mb-16 overflow-hidden rounded-xl shadow-md border border-gray-100">
              <MapContainer
                center={[currentLocation.lat, currentLocation.lng]}
                zoom={13}
                className="w-full h-full"
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker
                  position={[currentLocation.lat, currentLocation.lng]}
                  icon={customMarkerIcon}
                >
                  <Popup>
                    Current Location: {orderTrackingStatusDetails.shipping_address?.city || 'Unknown'}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200 max-w-2xl mx-auto p-8">
            <div className="mb-6">
              <IconPackage size={48} className="mx-auto text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-800 mb-3">
              {searchedOrderId ? 'Order Not Found' : 'Track Your Order'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchedOrderId 
                ? "We couldn't find an order with that ID. Please check the number and try again."
                : "Enter your order ID above to view your order status and tracking information."}
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  setInputOrderId('');
                  document.getElementById('order-id')?.focus();
                }}
                className="bg-[#00148c] text-white px-6 py-2 rounded-lg hover:bg-[#0026d2] transition-colors duration-200"
              >
                {searchedOrderId ? 'Try Again' : 'Start Tracking'}
              </button>
              {searchedOrderId && (
                <button
                  onClick={() => setSearchedOrderId('')}
                  className="border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}